import { FormEvent, useState } from "react"
import { useRouter } from "next/router"
import { HiOutlineCube, HiOutlineMail } from "react-icons/hi"
import EmailInput from "../../components/form/EmailInput"
import Select, { ISelectProps } from "../../components/form/Select"
import {
  BoatIcon,
  BoxIcon,
  CarIcon,
  FreightIcon,
  FurnitureIcon,
  HomeIcon,
  MotorcycleIcon,
  PawIcon,
  TractorIcon,
} from "../../components/svgs"

import { EMAIL_STORAGE } from "../../constants/storage"
import { generateFormQueryParams } from "../../services/links"

const placeholder: ISelectProps["placeholder"] = {
  label: "What are you shipping?",
  icon: <HiOutlineCube className="h-6 w-6 stroke-blue14" />,
}

const shipOptions = {
  animals: [
    {
      Dogs: "dogs",
      Cats: "cats",
      Horses: "horses",
      "Other animals and pets": "others",
    },
  ],
  motorcycles: [
    {
      "Racing Bikes": "racing-bikes",
      "Dirt Bikes": "dirt-bikes",
      Scooters: "scooters",
      "Cruising Bikes / Choppers": "choppers",
      ATVs: "atvs",
      "3-Wheelers": "3-wheelers",
      "Other Motorcycles": "others",
    },
  ],
  vehicles: [
    {
      "Cars and Light Trucks": "cars",
      RVs: "rvs",
      "Camper Trailers": "trailers",
      "Oversized and Lifted Trucks": "trucks",
      "Antique and Special Care Vehicles": "antique-special-care",
      "Other Vehicles": "others",
    },
  ],
  boats: [
    {
      "Powerboats & Motorboats": "power-boats",
      "Fishing Boats": "fishing-boats",
      Sailboats: "sailboats",
      "Other boats": "others",
    },
  ],
  "household-items": [
    {
      Furniture: "furniture",
      "Kitchen Appliances": "kitchen",
      Boxes: "boxes",
    },
  ],
  "apartment-home-moves": [
    {
      "1 Bedroom Apartment": "1-bedrooms",
      "2 Bedroom Apartment": "2-bedrooms",
      "3 Bedroom House": "3-bedrooms",
      "4 Bedroom House": "4-bedrooms",
      "5 Bedroom House": "5-bedrooms",
    },
  ],
  "heavy-equipment": [
    {
      Tractors: "tractors",
      Excavators: "excavators",
      "Backhoe Loaders": "backhoe-loaders",
      "Skid Steer Loaders": "skid-steer-loaders",
      "Forklifts and Telehandlers": "forklifts-telehandlers",
      "Other Heavy Items": "others",
    },
  ],
  freight: [
    {
      "Less than Truckload": "less-than-truckload",
      "Full Truckload": "full-truckload",
    },
  ],
  others: [],
}

const options: ISelectProps["options"] = [
  {
    value: "animals",
    label: "Pets and animals",
    icon: <PawIcon className="h-6 w-6 stroke-blue14" />,
  },
  {
    value: "motorcycles",
    label: "Motorcycles",
    icon: <MotorcycleIcon className="h-6 w-6 stroke-blue14" />,
  },
  {
    value: "vehicles",
    label: "Vehicles",
    icon: <CarIcon className="h-6 w-6 stroke-blue14" />,
  },
  {
    value: "boats",
    label: "Boats and water vehicles",
    icon: <BoatIcon className="h-6 w-6 stroke-blue14" />,
  },
  {
    value: "household-items",
    label: "Household items",
    icon: <FurnitureIcon className="h-6 w-6 stroke-blue14" />,
  },
  {
    value: "apartment-home-moves",
    label: "Apartment and home moves",
    icon: <HomeIcon className="h-6 w-6 stroke-blue14" />,
  },
  {
    value: "heavy-equipment",
    label: "Heavy equipment and industrial",
    icon: <TractorIcon className="h-6 w-6 stroke-blue14" />,
  },
  {
    value: "freight",
    label: "Freight",
    icon: <FreightIcon className="h-6 w-6 stroke-blue14" />,
  },
  {
    value: "others",
    label: "All other items",
    icon: <BoxIcon className="h-6 w-6 stroke-blue14" />,
  },
]

export default function CostToShipForm() {
  const router = useRouter()
  const [selectInvalid, setSelectInvalid] = useState<boolean>()
  const [category, setCategory] = useState<string>()

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const email = form.get("CostToShip/email")
    const select = form.get("CostToShip/select")

    setSelectInvalid(!select)
    if (!select) return

    const route = `/shipments/new/${select}`
    let queryParams: Record<string, string | number> = {}
    if (email) {
      queryParams.email = email.toString()
      sessionStorage.setItem(EMAIL_STORAGE, email.toString())
    }
    const formQueryParams = generateFormQueryParams({
      query: queryParams,
    })
    router.push(`${route}${formQueryParams}`)
  }

  return (
    <form action="" onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Select
        options={options}
        placeholder={placeholder}
        name="CostToShip/select"
        className="font-montserrat"
        invalid={selectInvalid}
        onSelect={(e) => setCategory(e.value)}
      />

      <EmailInput
        label="Email"
        name="CostToShip/email"
        className="bg-blue16 font-montserrat"
      >
        <HiOutlineMail className="h-6 w-6 stroke-blue14" />
      </EmailInput>
      <p className="my-2 text-center text-sm text-gray9">
        We'll keep your email secure. <br className="sm:hidden" /> Unsubscribe
        anytime.
      </p>
      <button
        type="submit"
        className="w-full m-auto py-5 rounded bg-blue7 text-white text-xl text-[22px] font-montserrat font-bold"
      >
        Get Free Transport Quotes
      </button>
    </form>
  )
}
